import { buildOutfitV2 } from '../cloth';
import type { FramingMesh } from '../framing';
import type { Body } from '../garments';
import { compositeBody, graftHead, type Donor } from '../heads';

import { dyeClothStyle } from '../palette';
import { buildWeapons, type WeaponGrip } from '../weapons';
import type { AtelierBoots, AtelierCharm, AtelierColors } from '../../../shared/atelier';
import { buildAccessories } from '../accessories';
import { gripHands } from '../grip';
import type { EquipmentWeaponId } from '../../../shared/equipment';
import { smoothNormals } from './normals';
import type { AtelierDraw } from '../../../shared/atelier-render';
export type { AtelierDraw, AtelierDrawMesh } from '../../../shared/atelier-render';

/** Arrays reference immutable source assets where possible; consumers must not mutate them.
 * Geometry can be shared by several actors. Their poses and uniforms cannot. */
export interface AtelierAssembly {
  draws: AtelierDraw[];
  triangles: number;
  framing: FramingMesh[];
  headFraming: FramingMesh[];
  contactHead: { center:[number,number,number]; neck:[number,number,number]; radius:number };
  weaponGrips?: WeaponGrip[];
}

export interface AssemblyInput {
  body: Body;
  attributes: Pick<Donor, 'nrm' | 'uv' | 'aux' | 'repair'>;
  /** Omit for the body's matching head. Already fitted in the donor's frame. */
  head?: Donor;
  outfit: string;
  earScale?: number;
  dye?: string;
  colors?: AtelierColors;
  weapon?: EquipmentWeaponId;
  boots?: AtelierBoots;
  charm?: AtelierCharm;
}

function usedVertices(count: number, indices: ArrayLike<number>): Uint8Array {
  const mask = new Uint8Array(count);
  for (let i = 0; i < indices.length; i++) mask[indices[i]] = 1;
  return mask;
}

/** Preserve the visible part of a skin triangle crossing a short cuff. Whole
 * triangle coverage left long wedges underneath the sleeve, which emerged
 * through the cloth when arm and chest weights bent in opposite directions. */
export function clipCuffSkin(draw: AtelierDraw, cut: Float32Array | undefined, offset = 0): AtelierDraw {
  if (!cut) return draw;
  const count = draw.pos.length / 3, edges = new Map<string, number>();
  const added: { a:number; b:number; t:number }[] = [], indices: number[] = [];
  const edge = (a:number, b:number):number => {
    if (a > b) [a,b] = [b,a];
    const key = `${a}/${b}`, prior = edges.get(key); if (prior !== undefined) return prior;
    const t = cut[a + offset] / (cut[a + offset] - cut[b + offset]);
    const id = count + added.length; added.push({a,b,t}); edges.set(key,id); return id;
  };
  for (let i = 0; i < draw.indices.length; i += 3) {
    const triangle = Array.from(draw.indices.slice(i,i+3)), fields = triangle.map(v => cut[v + offset]);
    if (fields.some(v => !Number.isFinite(v))) { indices.push(...triangle); continue; }
    const outside = fields.map(v => v <= 0);
    if (outside.every(Boolean)) { indices.push(...triangle); continue; }
    if (!outside.some(Boolean)) continue;
    const polygon:number[] = [];
    for (let k=0;k<3;k++) {
      const next=(k+1)%3;
      if (outside[k]) polygon.push(triangle[k]);
      if (outside[k] !== outside[next]) polygon.push(edge(triangle[k],triangle[next]));
    }
    for (let k=1;k+1<polygon.length;k++) indices.push(polygon[0],polygon[k],polygon[k+1]);
  }
  if (!added.length && indices.length === draw.indices.length) return draw;
  const interpolate = (source:ArrayLike<number>, stride:number, normalize=false):Float32Array => {
    const out=new Float32Array((count+added.length)*stride);out.set(Array.from(source));
    for (let i=0;i<added.length;i++) {
      const {a,b,t}=added[i];let length=0;
      for (let k=0;k<stride;k++) {const value=source[a*stride+k]*(1-t)+source[b*stride+k]*t;out[(count+i)*stride+k]=value;length+=value*value;}
      if(normalize){length=Math.sqrt(length)||1;for(let k=0;k<stride;k++)out[(count+i)*stride+k]/=length;}
    }
    return out;
  };
  const joints = new Uint8Array((count+added.length)*4), weights = new Float32Array(joints.length);
  joints.set(Array.from(draw.joints));weights.set(Array.from(draw.weights));
  for(let i=0;i<added.length;i++) {
    const {a,b,t}=added[i], blend=new Map<number,number>();
    for(const [v,f] of [[a,1-t],[b,t]])for(let k=0;k<4;k++){const j=draw.joints[v*4+k];blend.set(j,(blend.get(j)??0)+draw.weights[v*4+k]*f);}
    const top=[...blend].sort((a,b)=>b[1]-a[1]).slice(0,4), sum=top.reduce((s,[,v])=>s+v,0)||1;
    top.forEach(([j,v],k)=>{joints[(count+i)*4+k]=j;weights[(count+i)*4+k]=v/sum;});
  }
  return {...draw,pos:interpolate(draw.pos,3),nrm:interpolate(draw.nrm,3,true),outlineNrm:interpolate(draw.outlineNrm,3,true),
    uv:interpolate(draw.uv,2),aux:interpolate(draw.aux,4),repair:draw.repair?interpolate(draw.repair,3):undefined,joints,weights,indices:new Uint32Array(indices)};
}

/** Extracted from the constructor's assembly path. The live constructor remains
 * on its current renderer until parity and the renderer adapter are approved. */
export function assembleAtelierGeometry(input: AssemblyInput): AtelierAssembly {
  const graft = input.head ? graftHead(input.body, input.head, input.attributes, input.earScale ?? 1) : null;
  const body = graft ? compositeBody(input.body, graft) : input.body;
  const base = graft?.base ?? input.body;
  const attributes = graft?.base ?? input.attributes;
  const baseCount = base.pos.length / 3;
  const outfit = buildOutfitV2(body, input.outfit, Boolean(graft));
  const visible: number[] = [], bodyIndices: number[] = [], headIndices: number[] = [];
  for (let t = 0; t < body.indices.length; t += 3) {
    const a = body.indices[t], b = body.indices[t + 1], c = body.indices[t + 2];
    if (outfit.covered[a] && outfit.covered[b] && outfit.covered[c]) continue;
    visible.push(a, b, c);
    if (a < baseCount && b < baseCount && c < baseCount) bodyIndices.push(a, b, c);
    else if (a >= baseCount && b >= baseCount && c >= baseCount) headIndices.push(a - baseCount, b - baseCount, c - baseCount);
  }
  const draws: AtelierDraw[] = [clipCuffSkin({
    material: 'body', pos: base.pos, nrm: attributes.nrm, outlineNrm: attributes.nrm,
    uv: attributes.uv, aux: attributes.aux, repair: attributes.repair, joints: base.joints, weights: base.weights,
    indices: new Uint32Array(bodyIndices),
  },outfit.cuffCut)];
  if (graft) draws.push(clipCuffSkin({
    material: 'head', pos: graft.pos, nrm: graft.nrm, outlineNrm: smoothNormals(graft.pos, graft.indices),
    uv: graft.uv, aux: graft.aux, repair: graft.repair, joints: graft.joints, weights: graft.weights,
    indices: new Uint32Array(headIndices),
  },outfit.cuffCut,baseCount));
  if(input.weapon)draws[0]=gripHands(draws[0],body.sk,input.weapon);
  for (const piece of outfit.pieces) {
    const nrm = smoothNormals(piece.positions, piece.indices);
    draws.push({
      material: 'cloth', id: piece.id, style: dyeClothStyle(piece.style,piece.id,input.dye,input.colors), center: piece.center,
      pos: piece.positions, nrm, outlineNrm: nrm, uv: piece.uv, aux: piece.aux,
      joints: piece.joints, weights: piece.weights, indices: new Uint32Array(piece.indices),
    });
  }
  let skinFrame: FramingMesh = { pos: body.pos, joints: body.joints, weights: body.weights, mask: usedVertices(body.pos.length / 3, visible) };
  if(outfit.cuffCut) {
    const skinDraws=draws.filter(draw=>draw.material!=='cloth'), indices:number[]=[];let offset=0;
    for(const draw of skinDraws){indices.push(...Array.from(draw.indices,i=>i+offset));offset+=draw.pos.length/3;}
    // Cut vertices also belong to the framing mesh: a hybrid head's index
    // offset follows the final body packet, rather than its cached source.
    skinFrame={pos:Float32Array.from(skinDraws.flatMap(draw=>Array.from(draw.pos))),
      joints:Uint8Array.from(skinDraws.flatMap(draw=>Array.from(draw.joints))),
      weights:Float32Array.from(skinDraws.flatMap(draw=>Array.from(draw.weights))),mask:usedVertices(offset,indices)};
  }
  const framing: FramingMesh[] = [
    skinFrame,
    ...outfit.pieces.map(piece => ({ pos: piece.positions, joints: piece.joints, weights: piece.weights,
      mask: usedVertices(piece.positions.length / 3, piece.indices) })),
  ];
  const weapons=input.weapon?buildWeapons(body.sk,input.weapon,input.colors):null;
  if(weapons) {
    draws.push(...weapons.draws);
    framing.push(...weapons.draws.map(draw=>({pos:draw.pos,joints:draw.joints,weights:draw.weights})));
  }
  const accessories=buildAccessories(body,attributes,input.boots,input.charm,input.colors);
  draws.push(...accessories);
  framing.push(...accessories.map(draw=>({pos:draw.pos,joints:draw.joints,weights:draw.weights})));
  const headFraming = framing.map(mesh => {
    const mask = new Uint8Array(mesh.pos.length / 3);
    for (let i = 0; i < mask.length; i++) {
      if (mesh.mask && !mesh.mask[i]) continue;
      let weight = 0;
      for (let k = 0; k < 4; k++) {
        const chain = body.sk.bones[mesh.joints[i * 4 + k]]?.chain;
        if (chain === 'head' || chain === 'ear') weight += mesh.weights[i * 4 + k];
      }
      mask[i] = weight > .45 ? 1 : 0;
    }
    return { ...mesh, mask };
  });
  return { draws, triangles: draws.reduce((count, draw) => count + draw.indices.length / 3, 0), framing, headFraming,
    contactHead:{center:[...body.lm.head],neck:[...body.lm.neck],radius:body.lm.radius.head},weaponGrips:weapons?.grips };
}
